/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { ProductMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import Search from 'antd/es/input/Search';
import {
  IProductAllocation,
  IProductAllocationReq,
} from '@/interfaces/product';
import ProductAllocationTable from './components/product-allocation-table';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { queryProductAllocations } from '@/adapters/product';
import { IBusinessUnit } from '@/interfaces/business-unit';
import { queryBusinessUnits } from '@/adapters/business-unit';
import ExcelExport from './components/export-excel';
import ImportExport from './components/import-excel';
import { debounce } from 'lodash';
import { isWareHouse } from '@/utils/permissionUtils';

const ProductAllocation: React.FC = () => {
  const pathSegment = getPathSegment();

  const perWarehouse = isWareHouse();

  const [criteria, setCriteria] = useState<IProductAllocationReq>({
    name: '',
    search: '',
  } as IProductAllocationReq);

  const {
    data: allocations,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<AxiosResponse<IProductAllocation[]>, Error>({
    queryKey: ['search-product-allocations'],
    queryFn: async () => await queryProductAllocations(),
  });

  const { data: bus } = useQuery<AxiosResponse<IBusinessUnit[]>, Error>({
    queryKey: ['query-bus'],
    queryFn: async () => await queryBusinessUnits(),
  });

  const debouncedSearch = useCallback(
    debounce(value => {
      setCriteria({
        ...criteria,
        search: value,
      });
    }, 300),
    [],
  );

  const searchTerm = criteria?.search?.toLowerCase();
  const filterAllocations = searchTerm
    ? allocations?.data?.filter(({ ProductCode, ProductName }) =>
        [ProductCode, ProductName].some(field =>
          field.toLowerCase().includes(searchTerm),
        ),
      )
    : allocations?.data;

  return (
    <>
      <SubMenu active={pathSegment} items={ProductMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Product Allocations">
          <div className="flex flex-col justify-end md:flex-row gap-2">
            {!perWarehouse && (
              <ImportExport
                bus={bus?.data || []}
                onImported={() => refetch()}
              />
            )}
            <ExcelExport bus={bus?.data || []} data={allocations?.data || []} />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={debouncedSearch}
              />
            </div>
            <div className="flex justify-end gap-3"></div>
          </div>
          <ProductAllocationTable
            criteria={criteria}
            product={{
              data: filterAllocations || [],
              totalRecords: 0,
            }}
            bus={bus?.data || []}
            loading={isLoading || isRefetching}
          />
        </div>
      </Card>
    </>
  );
};

export default ProductAllocation;
